/* eslint-disable no-shadow */
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes, { any } from 'prop-types';

import DrawPlayerItemV2 from './DrawPlayerItemV2';
import DrawTeamItemV2 from './DrawTeamItemV2';
import PopOverMenu from '../../components/PopOverMenu';
import Img from '../../components/Img';

import { isEventOfficialHsEvent, isDrawMainOrQualifying, isDoublesFormat, isSinglesFormat, isTennisSportTypeEvent, isPickleBallSportTypeEvent, isDrawAutofillEnabledForCategory, shouldAllowPlayerListActionsEventDesk, isDrawAutofillEnabledForEliminationType } from '../../utils/events';
import { arePlayersInTeamV2, doTeamsContainSamePlayers } from '../../utils/draws';
import { getSmsSendingAvailability } from '../../utils/eventSmsAvailability';
import { drawPlacementStatusTypeIds, drawEliminationTypeIds, drawCategoryTypeIds } from '../../utils/eventConstants';
import DebouncedFilterInput from '../../components/FormElementsStandalone/DebouncedFilterInput';
import { ratingTypes, seedTypeOptions } from '../../utils/constants';
import PopOverMenuMultiLevel from '../../components/PopOverMenuMultiLevel';
import ContactForm from './ContactForm';
import { useStatsig } from '../../components/hooks/useStatsig';

const DrawPlayersListV2 = (props) => {
    const { isReadOnly, utrType, playersFilter, placed, notPlaced, waitlist, available, notInDraw, notInDrawTeams, createTeam, showSplitModal, showMoveModal, showRemoveModal, addToDraw, showAutofillModal, handlePlayerPopoverClick, handleSortPlayersClick, selected, selectedTeams, selectedNotInDraw, selectedTeamsNotInDraw, preventSelectMove, selectedSection, teamsHaveResults, playersDictByMemberId, teamsDict, membersInDraw = [], teamsInDraw = [], membersInTeams = [], togglePlayerSelect, toggleNDPlayerSelect, toggleTeamSelect, toggleNDTeamSelect, setPlayerListFilter, handleSelectAll, toggleSectionSelect, sportTypeId, handleSeedSettingClick, selectedSeedType, selectedSeedPercentage, hideSeedSelection, handleHideSeedSettingClick, displaySeedingOrder, handleMemberSeedOrderChanged, handleTeamSeedOrderChanged, seedingManualOrderMember, seedingManualOrderTeam, setSeedingManualOrderMember, setSeedingManualOrderTeam } = props;
    const { teams, currentDraw, parentDraw, event, organizerRatings } = useSelector(({ drawEdit }) => {
        const { teams, currentDraw, parentDraw, event, organizerRatings } = drawEdit;
        return { teams, currentDraw, parentDraw, event, organizerRatings };
    });
    const draw = currentDraw.data || {};
    const isOfficialHSEvent = isEventOfficialHsEvent(event.data);
    const hasBackdraw = draw.eliminationTypeId === drawEliminationTypeIds.FIRST_MATCH_BACKDRAW && isDrawMainOrQualifying(draw.categoryTypeId);
    const isBackdraw = draw.categoryTypeId === drawCategoryTypeIds.BACK_DRAW;

    let drawHasScores = false;
    if (draw.rounds) {
        draw.rounds.forEach(round => {
            round.matches.forEach(match => {
                if (match.result) {
                    drawHasScores = true;
                }
            });
        });
    }
    const { checkGate, featureGates } = useStatsig();
    const newSeedingSettingsEnabled = checkGate(featureGates.NEW_SEEDING_SETTINGS);
    const smsAvailability = useMemo(() => getSmsSendingAvailability(event?.data), [event?.data]);
    const filteredAvailable = notPlaced?.length > 0 ? available.filter(el1 => !notPlaced.some(el2 => el2.members?.includes(el1.memberId))) : available;
    const selectedPlayersAreInDrawTeams = selected.some(memberId => membersInTeams.includes(memberId));
    const selectedTeamsNotInDrawPlayersAlreadyInDrawTeams = selectedTeamsNotInDraw.some(teamId => membersInTeams.includes(teamsDict[teamId].members[0]) || membersInTeams.includes(teamsDict[teamId].members[1]));
    const selectedTeamsNotInDrawContainSamePlayers = doTeamsContainSamePlayers(selectedTeamsNotInDraw, teamsDict);
    const playersCanBeInMultipleTeams = draw.eliminationTypeId === drawEliminationTypeIds.MATCH_PLAY || draw.eliminationTypeId === drawEliminationTypeIds.AD_HOC || draw.eliminationTypeId === drawEliminationTypeIds.ROUND_ROBIN;
    const showContact = selected.length > 0 || selectedTeams.length > 0;
    const allTeams = useSelector((state) => state.drawEdit.teams).data;

    const hasNotPlacedItems = !!notPlaced.length;
    const hasPlacedItems = !!placed.length;

    const [showContactForm, setShowContactForm] = useState(false);
    const [expandNotPlaced, setExpandNotPlaced] = useState(hasNotPlacedItems);
    const [expandPlaced, setExpandPlaced] = useState(hasPlacedItems);
    const [expandWaitlist, setExpandWaitlist] = useState(false);
    const [expandAvailable, setExpandAvailable] = useState(false);
    const [expandNotInDraw, setExpandNotInDraw] = useState(false);
    const [expandNotInDrawTeams, setExpandNotInDrawTeams] = useState(false);

    useEffect(() => {
        if ((notPlaced?.length && !expandNotPlaced) || (!notPlaced?.length && expandNotPlaced)) {
            setExpandNotPlaced(prevState => {
                return !prevState;
            });
        }
        if ((placed?.length && !expandPlaced) || (!placed?.length && expandPlaced)) {
            setExpandPlaced(prevState => {
                return !prevState;
            });
        }
    }, [placed, notPlaced]);

    useEffect(() => {
        if (expandNotInDraw) {
            setExpandNotInDraw(false);
        }
        if (expandNotInDrawTeams) {
            setExpandNotInDrawTeams(false);
        }
    }, [draw]);

    const openContactSidePanel = () => {
        setShowContactForm(true);
    };

    const closeSidePanel = () => {
        setShowContactForm(false);
    };

    const findMembersByTeamIds = (teams, teamIds) => {
        return teams?.reduce((members, team) => {
            if (teamIds?.includes(team?.id)) {
                return members?.concat(team?.members);
            }
            return members;
        }, []);
    };

    const membersList = findMembersByTeamIds(allTeams, selectedTeams);
    const showCreateTeam = shouldAllowPlayerListActionsEventDesk(draw.categoryTypeId) && selected.length === 2 && !selectedTeams.length && isDoublesFormat(draw.formatTypeId) && !arePlayersInTeamV2(selected, teams);
    const showSplitTeam = shouldAllowPlayerListActionsEventDesk(draw.categoryTypeId) && !preventSelectMove && selectedTeams.length > 0 && !selected.length;
    const showMoveCopy = shouldAllowPlayerListActionsEventDesk(draw.categoryTypeId) && (selected.length > 0 || selectedTeams.length > 0);
    const showRemove = shouldAllowPlayerListActionsEventDesk(draw.categoryTypeId) && !preventSelectMove && (selected.length > 0 || selectedTeams.length > 0) && !selectedPlayersAreInDrawTeams;
    const showAddToDraw = shouldAllowPlayerListActionsEventDesk(draw.categoryTypeId) && ((selectedNotInDraw.length > 0 || selectedTeamsNotInDraw.length > 0)) && !(!playersCanBeInMultipleTeams && (selectedTeamsNotInDrawPlayersAlreadyInDrawTeams || selectedTeamsNotInDrawContainSamePlayers));
    const areAnyPlayersTeamsSelected = selected.length || selectedTeams.length || selectedNotInDraw.length || selectedTeamsNotInDraw.length;
    const arePlayersOrTeamsInDraw = membersInDraw.length > 0 || teamsInDraw.length > 0;
    const showAutoFill = isDrawAutofillEnabledForCategory(draw.categoryTypeId) &&
        isDrawAutofillEnabledForEliminationType(draw.eliminationTypeId) &&
        arePlayersOrTeamsInDraw &&
        !drawHasScores &&
        !areAnyPlayersTeamsSelected;

    const canShowPlayer = (player) => {
        const playerListFilter = props.playerListFilter?.replace(/[-[\]{}()*+?.,\\/^$|#\s]/g, '\\$&');
        const regex = new RegExp(playerListFilter, 'i');
        return props.playerListFilter && !regex.exec(`${player.firstName}${player.lastName}`) && !regex.exec(player.searchableName);
    };

    const playerPanels = {
        NOT_PLACED: 'notPlaced',
        PLACED: 'placed',
        WAITLIST: 'waitlist',
        AVAILABLE: 'available',
        NOT_IN_DRAW: 'notInDraw',
        NOT_IN_DRAW_TEAMS: 'notInDrawTeams',
    };

    const togglePanel = (panelName) => {
        switch (panelName) {
            case playerPanels.NOT_PLACED:
                setExpandNotPlaced(!expandNotPlaced);
                break;
            case playerPanels.PLACED:
                setExpandPlaced(!expandPlaced);
                break;
            case playerPanels.WAITLIST:
                setExpandWaitlist(!expandWaitlist);
                break;
            case playerPanels.AVAILABLE:
                setExpandAvailable(!expandAvailable);
                break;
            case playerPanels.NOT_IN_DRAW:
                setExpandNotInDraw(!expandNotInDraw);
                break;
            case playerPanels.NOT_IN_DRAW_TEAMS:
                setExpandNotInDrawTeams(!expandNotInDrawTeams);
                break;
            default:
                break;
        }
    };

    return (
        <div className="draw-players-list">
            <div className="header">
                {!(selected.length || selectedTeams.length || selectedNotInDraw.length || selectedTeamsNotInDraw.length) && (
                    <span>
                        {teamsInDraw?.length > 0 && <span className="text-white text-uppercase" data-testid="draw-edit.players-list.teams-count">Teams ({teamsInDraw.length})</span>}
                        {membersInDraw?.length > 0 && teamsInDraw?.length > 0 ? ' / ' : ''}
                        {membersInDraw?.length > 0 && <span className="text-white text-uppercase" data-testid="draw-edit.players-list.players-count">Players ({membersInDraw.length})</span>}
                    </span>
                )}

                {showCreateTeam && (
                    <span className="clickable mr8" onClick={() => createTeam()} data-testid="draw-edit.players-list.create-team-btn">Create Team</span>
                )}

                {showSplitTeam && (
                    <span className="clickable mr8" onClick={() => showSplitModal()} data-testid="draw-edit.players-list.split-team-btn">
                        Split Team{selectedTeams.length > 1 ? 's' : ''}
                    </span>
                )}

                {showMoveCopy && (
                    <span className="clickable" onClick={() => showMoveModal(draw.id)} data-testid="draw-edit.players-list.move-btn">
                        {!preventSelectMove ? ' Move' : ' Copy'}
                    </span>
                )}

                {showRemove && (
                    <span className="clickable ml8" onClick={() => showRemoveModal(draw.id)} data-testid="draw-edit.players-list.remove-btn">Remove</span>
                )}

                {showAddToDraw && (
                    <span className="clickable ml8" onClick={() => addToDraw()} data-testid="draw-edit.players-list.add-to-draw-btn">Add To Draw</span>
                )}

                {showAutoFill && (
                    <span className="float-right clickable underline" onClick={() => showAutofillModal(draw.id, null, true)} data-testid="draw-edit.players-list.autofill-btn">Auto-Fill</span>
                )}

                {showContact && (
                    <span className="clickable ml8" onClick={openContactSidePanel} data-testid="draw-edit.players-list.contact-btn">Contact</span>
                )}
            </div>
            {newSeedingSettingsEnabled && (
                <div className="seeds-menu">
                    <div className="w200 fw500">
                        <PopOverMenuMultiLevel
                            popOverMenuClass="ml-auto"
                            menuItems={[
                        {
                            value: 1,
                            label: 'By rating',
                            subMenu: [
                                { value: 1, label: '25% of draw', seedingPercentage: 25 },
                                { value: 1, label: '50% of draw', seedingPercentage: 50 },
                                { value: 1, label: '100% of draw', seedingPercentage: 100 },
                            ],
                        },
                        {
                            value: 2,
                            label: 'Manually',
                        },
                        {
                            value: 3,
                            label: 'Random',
                            subMenu: [
                                { value: 3, label: '25% of draw', seedingPercentage: 25 },
                                { value: 3, label: '50% of draw', seedingPercentage: 50 },
                                { value: 3, label: '100% of draw', seedingPercentage: 100 },
                            ],
                        },
                    ]}
                            onSelected={handleSeedSettingClick}
                        ><span className="text-dark"> Seed: {seedTypeOptions[selectedSeedType]} {(selectedSeedType !== 2 && selectedSeedPercentage) && `${selectedSeedPercentage}%`} <i className="uts uts-expand" /> </span>
                        </PopOverMenuMultiLevel>
                    </div>
                    <div className="row">
                        <div className="col-12">
                            <input
                                type="checkbox"
                                name="seed-visibility"
                                id="seed-visibility"
                                className="standalone-checkbox dark"
                                checked={hideSeedSelection}
                                onChange={() => handleHideSeedSettingClick()}
                                data-testid="draw-edit.players-list.hide-seeds-checkbox"
                            />
                            <label htmlFor="seed-visibility" className="fw400 mb0" data-testid="draw-edit.players-list.hide-seeds-label"><span />Hide Seeds</label>
                        </div>
                    </div>
                </div>
            )}
            <div className="sub-header" style={{ position: 'absolute', top: '-53px' }}>
                <DebouncedFilterInput
                    value={props.playerListPlayer}
                    onChange={setPlayerListFilter}
                    placeholder="Filter Players"
                />
            </div>
            <div className="sub-header">
                {!isReadOnly && (
                    <div className="checkbox-wrapper" onClick={handleSelectAll} data-testid="draw-edit.players-list.select-all-checkbox">
                        <div className={`checkbox-check ${selectedSection.all ? 'checked' : ''}`}><Img src="/img/icons/checkmark-icon.svg" /></div>
                    </div>
                )}All
                {!(selected.length > 0 || selectedTeams.length > 0) && (
                    <PopOverMenu
                        popOverMenuClass="ml-auto"
                        data-testid="draw-edit.players-list.sort-menu"
                        menuItems={[
                            ...(isTennisSportTypeEvent(sportTypeId) ? [
                                { value: 'utr_asc', label: `${ratingTypes.tennis} (low to high)` },
                                { value: 'utr_desc', label: `${ratingTypes.tennis} (high to low)` },
                            ] : []),
                            ...(isPickleBallSportTypeEvent(sportTypeId) ? [
                                { value: 'pkbr_asc', label: `${ratingTypes.pickleball} (low to high)` },
                                { value: 'pkbr_desc', label: `${ratingTypes.pickleball} (high to low)` },
                            ] : []),
                            { value: 'first_asc', label: 'First Name (A to Z)' },
                            { value: 'first_desc', label: 'First Name (Z to A)' },
                            ...(isSinglesFormat(draw.formatTypeId) ? [{ value: 'last_asc', label: 'Last Name (A to Z)' }] : []),
                            ...(isSinglesFormat(draw.formatTypeId) ? [{ value: 'last_desc', label: 'Last Name (Z to A)' }] : []),
                            ...(isSinglesFormat(draw.formatTypeId) && displaySeedingOrder && seedingManualOrderMember.some(item => Number.isFinite(item.seedNumber)) ? [{ value: 'seed_asc', label: 'Seed (low to high)' }] : []),
                            ...(isSinglesFormat(draw.formatTypeId) && displaySeedingOrder && seedingManualOrderMember.some(item => Number.isFinite(item.seedNumber)) ? [{ value: 'seed_desc', label: 'Seed (high to low)' }] : []),
                            ...(!isSinglesFormat(draw.formatTypeId) && displaySeedingOrder && seedingManualOrderTeam.some(item => Number.isFinite(item.seedNumber)) ? [{ value: 'seed_asc', label: 'Seed (low to high)' }] : []),
                            ...(!isSinglesFormat(draw.formatTypeId) && displaySeedingOrder && seedingManualOrderTeam.some(item => Number.isFinite(item.seedNumber)) ? [{ value: 'seed_desc', label: 'Seed (high to low)' }] : []),
                        ]}
                        onSelected={handleSortPlayersClick}
                    ><span className="float-right"> Sort by <span className="text-dark">{playersFilter.label}</span> <i className="uts uts-expand" /> </span>
                    </PopOverMenu>
                )}

                {(selected.length > 0 || selectedTeams.length > 0) && (
                    <span className="text-dark ml-auto">
                        {selectedTeams.length > 0 && <span>{selectedTeams.length}/{teamsInDraw.length} teams</span>}
                        {selected.length > 0 && selectedTeams.length > 0 ? ' & ' : ''}
                        {selected.length > 0 && <span>{selected.length}/{membersInDraw.length} players</span>}
                        &nbsp;selected&nbsp;
                    </span>
                )}
            </div>
            <div className="title-header cursor-pointer" onClick={() => togglePanel(playerPanels.NOT_PLACED)} data-testid="draw-edit.players-list.not-placed-header">
                {!isReadOnly && (
                    <div className="checkbox-wrapper" onClick={(e) => toggleSectionSelect(e, drawPlacementStatusTypeIds.NOT_PLACED)} data-testid="draw-edit.players-list.not-placed-section-checkbox">
                        <div className={`checkbox-check ${selectedSection[drawPlacementStatusTypeIds.NOT_PLACED] ? 'checked' : ''}`}><Img src="/img/icons/checkmark-icon.svg" /></div>
                    </div>
                )}
                Not Placed ({notPlaced.length})
                <span className="ml-auto">
                    &nbsp;
                    <i className={`uts ${expandNotPlaced ? 'uts-collapse-arrow' : 'uts-expand-arrow'}`} />
                </span>
            </div>
            {expandNotPlaced && (
                <div className={`scrollable-playerlist`}>
                    {notPlaced.map(player => {
                        if (canShowPlayer(player)) {
                            return null;
                        }
                        return isSinglesFormat(draw.formatTypeId) ? (
                            <DrawPlayerItemV2
                                key={player.memberId}
                                isReadOnly={isReadOnly}
                                player={player}
                                isPlaced={player.draws[draw.id] && player.draws[draw.id].startingMatchPosition}
                                hasResult={player.draws[draw.id] && player.draws[draw.id].hasResultsInDraw}
                                isSelected={selected.includes(player.memberId)}
                                utrType={utrType}
                                showHighSchool={isOfficialHSEvent}
                                togglePlayerSelect={togglePlayerSelect}
                                handlePlayerPopoverClick={handlePlayerPopoverClick}
                                placementString="Not Placed"
                                hasBackdraw={hasBackdraw}
                                isBackdraw={isBackdraw}
                                organizerRatings={organizerRatings}
                                sportTypeId={sportTypeId}
                                drawId={draw.id}
                                parentDrawId={parentDraw.data?.id}
                                displaySeedingOrder={displaySeedingOrder}
                                handleMemberSeedOrderChanged={handleMemberSeedOrderChanged}
                                seedingManualOrder={seedingManualOrderMember}
                                setSeedingManualOrderMember={setSeedingManualOrderMember}
                                data-testid={`draw-edit.player-${player.memberId}`}
                            />
                        ) : (
                            <DrawTeamItemV2
                                key={player.id}
                                isReadOnly={isReadOnly}
                                teamId={player.id}
                                player1={player.members ? playersDictByMemberId[teamsDict[player.id].members[0]] : null}
                                player2={player.members ? playersDictByMemberId[teamsDict[player.id].members[1]] : null}
                                isSelected={selectedTeams.includes(player.id)}
                                utrType={utrType}
                                showHighSchool={isOfficialHSEvent}
                                toggleTeamSelect={toggleTeamSelect}
                                handlePlayerPopoverClick={handlePlayerPopoverClick}
                                placementString="Not Placed"
                                hasBackdraw={hasBackdraw}
                                isBackdraw={isBackdraw}
                                organizerRatings={organizerRatings}
                                sportTypeId={sportTypeId}
                                drawId={draw.id}
                                parentDrawId={parentDraw.data?.id}
                                displaySeedingOrder={displaySeedingOrder}
                                handleTeamSeedOrderChanged={handleTeamSeedOrderChanged}
                                seedingManualOrder={seedingManualOrderTeam}
                                setSeedingManualOrderTeam={setSeedingManualOrderTeam}
                                data-testid={`draw-edit.team-${player.id}`}
                            />
                        );
                    })}
                </div>
            )}
            {showContactForm && (
                <ContactForm
                    closeSidePanel={closeSidePanel}
                    selectedIndividualMemberIds={selected}
                    selectedMemberIdsFromTeams={membersList}
                    event={event}
                    smsSendingDisabled={!smsAvailability.canSend}
                    smsSendingDisabledReason={smsAvailability.reason}
                    smsSendingDisabledCutoffDisplay={smsAvailability.cutoffDisplay}
                />
            )}
            <div className="title-header cursor-pointer" onClick={() => togglePanel(playerPanels.PLACED)} data-testid="draw-edit.players-list.placed-header">
                {!isReadOnly && (
                    <div className="checkbox-wrapper" onClick={(e) => toggleSectionSelect(e, drawPlacementStatusTypeIds.PLACED)} data-testid="draw-edit.players-list.placed-section-checkbox">
                        <div className={`checkbox-check ${selectedSection[drawPlacementStatusTypeIds.PLACED] ? 'checked' : ''}`}><Img src="/img/icons/checkmark-icon.svg" /></div>
                    </div>
                )}
                Placed ({placed.length})
                <span className="ml-auto">
                    &nbsp;
                    <i className={`uts ${expandPlaced ? 'uts-collapse-arrow' : 'uts-expand-arrow'}`} />
                </span>
            </div>
            {expandPlaced && (
                <div className={`scrollable-playerlist`}>
                    {placed.map(player => {
                        if (canShowPlayer(player)) {
                            return null;
                        }
                        return isSinglesFormat(draw.formatTypeId) ? (
                            <DrawPlayerItemV2
                                key={player.memberId}
                                isReadOnly={isReadOnly}
                                player={player}
                                isPlaced={player.draws[draw.id] && player.draws[draw.id].startingMatchPosition}
                                hasResult={player.draws[draw.id] && player.draws[draw.id].hasResultsInDraw}
                                isSelected={selected.includes(player.memberId)}
                                togglePlayerSelect={togglePlayerSelect}
                                utrType={utrType}
                                showHighSchool={isOfficialHSEvent}
                                handlePlayerPopoverClick={handlePlayerPopoverClick}
                                hasBackdraw={hasBackdraw}
                                isBackdraw={isBackdraw}
                                organizerRatings={organizerRatings}
                                sportTypeId={sportTypeId}
                                drawId={draw.id}
                                parentDrawId={parentDraw.data?.id}
                                displaySeedingOrder={displaySeedingOrder}
                                handleMemberSeedOrderChanged={handleMemberSeedOrderChanged}
                                seedingManualOrder={seedingManualOrderMember}
                                setSeedingManualOrderMember={setSeedingManualOrderMember}
                                data-testid={`draw-edit.player-${player.memberId}`}
                            />
                        ) : (
                            <DrawTeamItemV2
                                key={player.id}
                                isReadOnly={isReadOnly}
                                teamId={player.id}
                                player1={player.members ? playersDictByMemberId[teamsDict[player.id].members[0]] : null}
                                player2={player.members ? playersDictByMemberId[teamsDict[player.id].members[1]] : null}
                                isSelected={selectedTeams.includes(player.id)}
                                utrType={utrType}
                                showHighSchool={isOfficialHSEvent}
                                toggleTeamSelect={toggleTeamSelect}
                                handlePlayerPopoverClick={handlePlayerPopoverClick}
                                teamHasResult={teamsHaveResults.includes(player.id)}
                                isPlaced={true}
                                hasBackdraw={hasBackdraw}
                                isBackdraw={isBackdraw}
                                organizerRatings={organizerRatings}
                                sportTypeId={sportTypeId}
                                drawId={draw.id}
                                parentDrawId={parentDraw.data?.id}
                                displaySeedingOrder={displaySeedingOrder}
                                handleTeamSeedOrderChanged={handleTeamSeedOrderChanged}
                                seedingManualOrder={seedingManualOrderTeam}
                                setSeedingManualOrderTeam={setSeedingManualOrderTeam}
                                data-testid={`draw-edit.team-${player.id}`}
                            />
                        );
                    })}
                </div>
            )}
            <div className="title-header cursor-pointer" onClick={() => togglePanel(playerPanels.WAITLIST)} data-testid="draw-edit.players-list.waitlist-header">
                {!isReadOnly && (
                    <div className="checkbox-wrapper" onClick={(e) => toggleSectionSelect(e, drawPlacementStatusTypeIds.WAITLIST)} data-testid="draw-edit.players-list.waitlist-section-checkbox">
                        <div className={`checkbox-check ${selectedSection[drawPlacementStatusTypeIds.WAITLIST] ? 'checked' : ''}`}><Img src="/img/icons/checkmark-icon.svg" /></div>
                    </div>
                )}
                Waitlist ({waitlist.length})
                <span className="ml-auto">
                    &nbsp;
                    <i className={`uts ${expandWaitlist ? 'uts-collapse-arrow' : 'uts-expand-arrow'}`} />
                </span>
            </div>
            {expandWaitlist && (
                <div className={`scrollable-playerlist`}>
                    {waitlist.map(player => {
                        if (canShowPlayer(player)) {
                            return null;
                        }
                        return isSinglesFormat(draw.formatTypeId) ? (
                            <DrawPlayerItemV2
                                key={player.memberId}
                                isReadOnly={isReadOnly}
                                player={player}
                                isPlaced={player.draws[draw.id] && player.draws[draw.id].startingMatchPosition}
                                hasResult={player.draws[draw.id] && player.draws[draw.id].hasResultsInDraw}
                                isSelected={selected.includes(player.memberId)}
                                togglePlayerSelect={togglePlayerSelect}
                                utrType={utrType}
                                showHighSchool={isOfficialHSEvent}
                                handlePlayerPopoverClick={handlePlayerPopoverClick}
                                isWaitlist={true}
                                placementString="Waitlist"
                                hasBackdraw={hasBackdraw}
                                isBackdraw={isBackdraw}
                                organizerRatings={organizerRatings}
                                sportTypeId={sportTypeId}
                                drawId={draw.id}
                                parentDrawId={parentDraw.data?.id}
                                data-testid={`draw-edit.player-${player.memberId}`}
                            />
                        ) : (
                            <DrawTeamItemV2
                                key={player.id}
                                isReadOnly={isReadOnly}
                                teamId={player.id}
                                player1={player.members ? playersDictByMemberId[teamsDict[player.id].members[0]] : null}
                                player2={player.members ? playersDictByMemberId[teamsDict[player.id].members[1]] : null}
                                isSelected={selectedTeams.includes(player.id)}
                                utrType={utrType}
                                showHighSchool={isOfficialHSEvent}
                                toggleTeamSelect={toggleTeamSelect}
                                handlePlayerPopoverClick={handlePlayerPopoverClick}
                                isWaitlist={true}
                                placementString="Waitlist"
                                hasBackdraw={hasBackdraw}
                                isBackdraw={isBackdraw}
                                organizerRatings={organizerRatings}
                                sportTypeId={sportTypeId}
                                drawId={draw.id}
                                parentDrawId={parentDraw.data?.id}
                                data-testid={`draw-edit.team-${player.id}`}
                            />
                        );
                    })}
                </div>
            )}

            {isDoublesFormat(draw.formatTypeId) && (
                <>
                    <div className="title-header cursor-pointer" onClick={() => togglePanel(playerPanels.AVAILABLE)} data-testid="draw-edit.players-list.available-header">
                        Available ({filteredAvailable.length})
                        <span className="ml-auto">
                            &nbsp;
                            <i className={`uts ${expandAvailable ? 'uts-collapse-arrow' : 'uts-expand-arrow'}`} />
                        </span>
                    </div>
                    {expandAvailable && (
                        <div className={`scrollable-playerlist`}>
                            {filteredAvailable.map(player => {
                                const playerListFilter = props.playerListFilter.replace(/[-[\]{}()*+?.,\\/^$|#\s]/g, '\\$&');
                                const regex = new RegExp(playerListFilter, 'i');
                                return props.playerListFilter && !(`${player.firstName}${player.lastName}`).match(regex) ? null : (
                                    <DrawPlayerItemV2
                                        key={player.memberId}
                                        isReadOnly={isReadOnly}
                                        player={player}
                                        isPlaced={player.draws[draw.id] && player.draws[draw.id].startingMatchPosition}
                                        hasResult={player.draws[draw.id] && player.draws[draw.id].hasResultsInDraw}
                                        isSelected={selected.includes(player.memberId)}
                                        hidePopOverMenu={true}
                                        togglePlayerSelect={togglePlayerSelect}
                                        utrType={utrType}
                                        showHighSchool={isOfficialHSEvent}
                                        handlePlayerPopoverClick={handlePlayerPopoverClick}
                                        showError={selected.includes(player.memberId) && !showCreateTeam && selected.length === 2 ? 'Team already exists' : false}
                                        hasBackdraw={hasBackdraw}
                                        isBackdraw={isBackdraw}
                                        organizerRatings={organizerRatings}
                                        sportTypeId={sportTypeId}
                                        drawId={draw.id}
                                        parentDrawId={parentDraw.data?.id}
                                        data-testid={`draw-edit.player-${player.memberId}`}
                                    />
                                );
                            })}
                        </div>
                    )}
                </>
            )}

            <div className="title-header cursor-pointer" onClick={() => togglePanel(playerPanels.NOT_IN_DRAW)} data-testid="draw-edit.players-list.not-in-draw-header">
                Players Not In Draw ({notInDraw.length})
                <span className="ml-auto">
                    &nbsp;
                    <i className={`uts ${expandNotInDraw ? 'uts-collapse-arrow' : 'uts-expand-arrow'}`} />
                </span>
            </div>
            {expandNotInDraw && (
                <div className={`scrollable-playerlist`}>
                    {notInDraw.map(player => {
                        const playerListFilter = props.playerListFilter.replace(/[-[\]{}()*+?.,\\/^$|#\s]/g, '\\$&');
                        const regex = new RegExp(playerListFilter, 'i');
                        return props.playerListFilter && !(`${player.firstName} ${player.lastName}`).match(regex) ? null : (
                            <DrawPlayerItemV2
                                key={player.memberId}
                                isReadOnly={isReadOnly}
                                player={player}
                                isPlaced={player.draws[draw.id] && player.draws[draw.id].startingMatchPosition}
                                hasResult={player.draws[draw.id] && player.draws[draw.id].hasResultsInDraw}
                                isSelected={selectedNotInDraw.includes(player.memberId)}
                                togglePlayerSelect={toggleNDPlayerSelect}
                                utrType={utrType}
                                showHighSchool={isOfficialHSEvent}
                                handlePlayerPopoverClick={handlePlayerPopoverClick}
                                isNotInDraw={true}
                                hasBackdraw={hasBackdraw}
                                isBackdraw={isBackdraw}
                                organizerRatings={organizerRatings}
                                sportTypeId={sportTypeId}
                                drawId={draw.id}
                                parentDrawId={parentDraw.data?.id}
                                data-testid={`draw-edit.player-${player.memberId}`}
                            />
                        );
                    })}
                </div>
            )}

            {isDoublesFormat(draw.formatTypeId) && (
                <>
                    <div className="title-header cursor-pointer" onClick={() => togglePanel(playerPanels.NOT_IN_DRAW_TEAMS)} data-testid="draw-edit.players-list.not-in-draw-teams-header">
                        Teams Not In Draw ({notInDrawTeams.length})
                        <span className="ml-auto">
                            &nbsp;
                            <i className={`uts ${expandNotInDrawTeams ? 'uts-collapse-arrow' : 'uts-expand-arrow'}`} />
                        </span>
                    </div>
                    {expandNotInDrawTeams && (
                        <div className={`scrollable-playerlist`}>
                            {notInDrawTeams.map(player => {
                                return canShowPlayer(player)
                                    ? null
                                    : (
                                        <DrawTeamItemV2
                                            key={player.id}
                                            isReadOnly={isReadOnly}
                                            teamId={player.id}
                                            player1={player.members ? playersDictByMemberId[teamsDict[player.id].members[0]] : null}
                                            player2={player.members ? playersDictByMemberId[teamsDict[player.id].members[1]] : null}
                                            isSelected={selectedTeamsNotInDraw.includes(player.id)}
                                            utrType={utrType}
                                            showHighSchool={isOfficialHSEvent}
                                            toggleTeamSelect={toggleNDTeamSelect}
                                            handlePlayerPopoverClick={handlePlayerPopoverClick}
                                            isNotInDraw={true}
                                            hasBackdraw={hasBackdraw}
                                            isBackdraw={isBackdraw}
                                            sportTypeId={sportTypeId}
                                            drawId={draw.id}
                                            parentDrawId={parentDraw.data?.id}
                                            data-testid={`draw-edit.team-${player.id}`}
                                        />
                                    );
                            })}
                        </div>
                    )}
                </>
            )}

        </div>
    );
};

DrawPlayersListV2.propTypes = {
    isReadOnly: PropTypes.bool,
    utrType: PropTypes.string,
    playersFilter: PropTypes.objectOf(any),
    placed: PropTypes.arrayOf(any),
    notPlaced: PropTypes.arrayOf(any),
    waitlist: PropTypes.arrayOf(any),
    available: PropTypes.arrayOf(any),
    notInDraw: PropTypes.arrayOf(any),
    notInDrawTeams: PropTypes.arrayOf(any),
    createTeam: PropTypes.func,
    showSplitModal: PropTypes.func,
    showMoveModal: PropTypes.func,
    showRemoveModal: PropTypes.func,
    addToDraw: PropTypes.func,
    showAutofillModal: PropTypes.bool,
    handlePlayerPopoverClick: PropTypes.func,
    handleSortPlayersClick: PropTypes.func,
    selected: PropTypes.arrayOf(any),
    selectedTeams: PropTypes.arrayOf(any),
    selectedNotInDraw: PropTypes.arrayOf(any),
    selectedTeamsNotInDraw: PropTypes.arrayOf(any),
    preventSelectMove: PropTypes.bool,
    selectedSection: PropTypes.objectOf(any),
    teamsHaveResults: PropTypes.arrayOf(any),
    playersDictByMemberId: PropTypes.objectOf(any),
    teamsDict: PropTypes.objectOf(any),
    membersInDraw: PropTypes.arrayOf(any),
    teamsInDraw: PropTypes.arrayOf(any),
    membersInTeams: PropTypes.arrayOf(any),
    togglePlayerSelect: PropTypes.func,
    toggleNDPlayerSelect: PropTypes.func,
    toggleTeamSelect: PropTypes.func,
    toggleNDTeamSelect: PropTypes.func,
    setPlayerListFilter: PropTypes.func,
    handleSelectAll: PropTypes.func,
    toggleSectionSelect: PropTypes.func,
    sportTypeId: PropTypes.number,
    handleSeedSettingClick: PropTypes.func,
    selectedSeedType: PropTypes.number,
    selectedSeedPercentage: PropTypes.number,
    hideSeedSelection: PropTypes.bool,
    handleHideSeedSettingClick: PropTypes.func,
    displaySeedingOrder: PropTypes.bool,
    handleMemberSeedOrderChanged: PropTypes.func,
    handleTeamSeedOrderChanged: PropTypes.func,
    seedingManualOrderMember: PropTypes.arrayOf(any),
    seedingManualOrderTeam: PropTypes.arrayOf(any),
    setSeedingManualOrderMember: PropTypes.func,
    setSeedingManualOrderTeam: PropTypes.func,
};

DrawPlayersListV2.defaultProps = {
    isReadOnly: false,
    utrType: '',
    playersFilter: {},
    placed: [],
    notPlaced: [],
    waitlist: [],
    available: [],
    notInDraw: [],
    notInDrawTeams: [],
    createTeam: () => {},
    showSplitModal: () => {},
    showMoveModal: () => {},
    showRemoveModal: () => {},
    addToDraw: () => {},
    showAutofillModal: false,
    handlePlayerPopoverClick: () => {},
    handleSortPlayersClick: () => {},
    selected: [],
    selectedTeams: [],
    selectedNotInDraw: [],
    selectedTeamsNotInDraw: [],
    preventSelectMove: false,
    selectedSection: {},
    teamsHaveResults: [],
    playersDictByMemberId: {},
    teamsDict: {},
    membersInDraw: [],
    teamsInDraw: [],
    membersInTeams: [],
    togglePlayerSelect: () => {},
    toggleNDPlayerSelect: () => {},
    toggleTeamSelect: () => {},
    toggleNDTeamSelect: () => {},
    setPlayerListFilter: () => {},
    handleSelectAll: () => {},
    toggleSectionSelect: () => {},
    sportTypeId: 0,
    handleSeedSettingClick: () => {},
    selectedSeedType: 0,
    selectedSeedPercentage: 25,
    hideSeedSelection: false,
    handleHideSeedSettingClick: () => {},
    displaySeedingOrder: false,
    handleMemberSeedOrderChanged: () => {},
    handleTeamSeedOrderChanged: () => {},
    seedingManualOrderMember: [],
    seedingManualOrderTeam: [],
    setSeedingManualOrderMember: () => { },
    setSeedingManualOrderTeam: () => { },
};

export default DrawPlayersListV2;
